import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkAnomalies } from '../../api/ai';
import type { AnomalyItem } from '../../api/ai';
import styles from './AnomalyBanner.module.css';

interface Props {
  estimationData: Record<string, unknown>;
  projectId?: string;
}

const LANG_MAP: Record<string, string> = {
  fr: 'French',
  de: 'German',
  it: 'Italian',
  en: 'English',
};

const SEVERITY_CONFIG = {
  critical: { icon: '\u26D4', cls: 'critical' },
  warning: { icon: '\u26A0\uFE0F', cls: 'warning' },
  info: { icon: '\u2139\uFE0F', cls: 'info' },
} as const;

export function AnomalyBanner({ estimationData, projectId }: Props) {
  const { t, i18n } = useTranslation();
  const [anomalies, setAnomalies] = useState<AnomalyItem[]>([]);
  const [overallStatus, setOverallStatus] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(false);

  async function runCheck() {
    try {
      setLoading(true);
      setError(false);
      const result = await checkAnomalies({
        estimation_data: estimationData,
        session_id: projectId,
        language: LANG_MAP[i18n.language] || 'French',
      });
      setAnomalies(result.anomalies);
      setOverallStatus(result.overall_status);
      setChecked(true);
      if (result.anomalies.length > 0) {
        setExpanded(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  // Not yet checked
  if (!checked) {
    return (
      <div className={styles.banner}>
        <button
          className={styles.checkBtn}
          onClick={runCheck}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className={styles.spinner} />
              {t('common:checkingAnomalies', 'Checking for anomalies...')}
            </>
          ) : (
            <>
              <span className={styles.shieldIcon}>&#x1F6E1;</span>
              {t('common:runSanityCheck', 'Run sanity check')}
            </>
          )}
        </button>
        {error && (
          <span className={styles.errorText}>
            {t('common:checkFailed', 'Failed to run check.')}{' '}
            <button className={styles.retryLink} onClick={runCheck}>{t('common:retry', 'Retry')}</button>
          </span>
        )}
      </div>
    );
  }

  // No anomalies found
  if (anomalies.length === 0) {
    return (
      <div className={`${styles.result} ${styles.resultOk}`}>
        <span className={styles.checkmark}>&#x2705;</span>
        <span>{t('common:noAnomalies', 'All metrics within normal range. No anomalies detected.')}</span>
        <button className={styles.recheckBtn} onClick={runCheck}>{t('common:recheck', 'Re-check')}</button>
      </div>
    );
  }

  // Anomalies found
  const statusCls = overallStatus === 'critical' ? styles.resultCritical
    : overallStatus === 'warning' ? styles.resultWarning
    : styles.resultInfo;

  return (
    <div className={`${styles.result} ${statusCls}`}>
      <div className={styles.resultHeader} onClick={() => setExpanded(!expanded)}>
        <span className={styles.alertIcon}>
          {overallStatus === 'critical' ? '\u26D4' : '\u26A0\uFE0F'}
        </span>
        <span className={styles.resultSummary}>
          {t('common:anomaliesDetected', '{{count}} anomalies detected', { count: anomalies.length })}
        </span>
        <span className={styles.expandIcon}>{expanded ? '\u25B2' : '\u25BC'}</span>
        <button className={styles.recheckBtn} onClick={(e) => { e.stopPropagation(); runCheck(); }}>
          {t('common:recheck', 'Re-check')}
        </button>
      </div>

      {expanded && (
        <div className={styles.anomalyList}>
          {anomalies.map((a, i) => {
            const sev = SEVERITY_CONFIG[a.severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.info;
            return (
              <div key={i} className={`${styles.anomalyCard} ${styles[sev.cls]}`}>
                <div className={styles.anomalyHeader}>
                  <span className={styles.sevIcon}>{sev.icon}</span>
                  <span className={styles.anomalyMetric}>{a.metric}</span>
                  <span className={`${styles.sevBadge} ${styles[`badge_${sev.cls}`]}`}>
                    {t(`common:severity_${sev.cls}`, sev.cls)}
                  </span>
                  <span className={styles.zScore}>z={a.z_score}</span>
                </div>
                <div className={styles.anomalyValues}>
                  <span>Current: <strong>{formatMetricValue(a.current_value)}</strong></span>
                  <span className={styles.separator}>|</span>
                  <span>Reference: {formatMetricValue(a.reference_mean)} (±{formatMetricValue(a.reference_std)})</span>
                </div>
                {a.explanation && (
                  <p className={styles.explanation}>{a.explanation}</p>
                )}
                {a.recommendation && (
                  <p className={styles.recommendation}>
                    <span className={styles.recIcon}>&#x1F4A1;</span>
                    {a.recommendation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatMetricValue(val: number): string {
  if (Math.abs(val) >= 10000) {
    return val.toLocaleString('fr-CH', { maximumFractionDigits: 0 });
  }
  if (Math.abs(val) < 1) {
    return (val * 100).toFixed(1) + '%';
  }
  return val.toLocaleString('fr-CH', { maximumFractionDigits: 2 });
}
